import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Bell, Calendar, Package, Mail as MailIcon, TrendingUp, CreditCard,
  CheckCircle2, ExternalLink, RefreshCw, Eye, Send, Copy, AlertTriangle,
} from "lucide-react";
import { adminClient } from "./api";
import AlertSettingsCard from "./AlertSettingsCard";
import CustomerEmailsCard from "./CustomerEmailsCard";
import CalendarSyncCard from "./CalendarSyncCard";
import IncomeCard from "./IncomeCard";

const KIND_META = {
  booking_created: { icon: Calendar, label: "Booking", color: "bg-[#FFDF00] text-[#0C2E2B]" },
  order_created: { icon: Package, label: "Order", color: "bg-[#0C2E2B] text-[#FFDF00]" },
  inquiry_created: { icon: MailIcon, label: "Inquiry", color: "bg-emerald-600 text-white" },
  weekly_digest: { icon: TrendingUp, label: "Digest", color: "bg-blue-600 text-white" },
  sms_optout: { icon: Bell, label: "SMS opt-out", color: "bg-red-600 text-white" },
  sms_inbound: { icon: MailIcon, label: "Text reply", color: "bg-violet-600 text-white" },
  damage_claim: { icon: AlertTriangle, label: "Damage claim", color: "bg-red-600 text-white" },
  low_stock: { icon: Package, label: "Low stock", color: "bg-amber-500 text-[#0C2E2B]" },
  payment_received: { icon: CreditCard, label: "Payment", color: "bg-emerald-700 text-white" },
  store_not_ready: { icon: AlertTriangle, label: "Pickup not ready", color: "bg-red-600 text-white" },
  store_ready: { icon: Package, label: "Pickup ready", color: "bg-emerald-600 text-white" },
};

function AlertRow({ alert, onMarkRead }) {
  const meta = KIND_META[alert.kind] || { icon: Bell, label: alert.kind, color: "bg-zinc-200 text-zinc-800" };
  const Icon = meta.icon;
  const created = new Date(alert.created_at);
  return (
    <div
      data-testid={`alert-row-${alert.id}`}
      className={`p-4 border-b border-zinc-100 last:border-0 flex items-start gap-3 transition-colors ${
        alert.read ? "bg-white" : "bg-[#FFDF00]/10 hover:bg-[#FFDF00]/20"
      }`}
    >
      <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${meta.color}`}>
        <Icon className="w-4 h-4" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-tactical uppercase tracking-widest text-zinc-500">{meta.label}</span>
          {!alert.read && <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />}
          <span className="text-[10px] text-zinc-400 ml-auto whitespace-nowrap">{created.toLocaleString()}</span>
        </div>
        <div className="font-bold text-sm text-[#0C2E2B] mt-0.5">{alert.title}</div>
        <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{alert.body}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {alert.kind === "low_stock" && alert.meta?.product_id && (
            <Link to={`/admin/merch?edit=${alert.meta.product_id}`} data-testid={`alert-restock-${alert.id}`} className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-md bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#E5C900]">
              <Package className="w-3 h-3" /> Restock now
            </Link>
          )}
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${alert.sms?.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
            SMS · {alert.sms?.status || "?"}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${alert.email?.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
            Email · {alert.email?.status || "?"}
          </span>
          {!alert.read && (
            <button
              data-testid={`alert-mark-read-${alert.id}`}
              onClick={() => onMarkRead(alert.id)}
              className="ml-auto text-[10px] font-bold text-[#0C2E2B] hover:underline flex items-center gap-1"
            >
              <CheckCircle2 className="w-3 h-3" /> Mark read
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StripeStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [copied, setCopied] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/stripe/status");
      setStatus(data);
    } catch { setStatus(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isLive = status?.mode === "live";
  const claimed = status?.account?.details_submitted;

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied((c) => ({ ...c, [key]: true }));
    toast.success("Copied");
    setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1500);
  };

  const envSnippet = `# Replace the four Stripe values in /app/backend/.env
STRIPE_SECRET_KEY="sk_live_xxxxxxxxxxxxxxxxxxxxxxxx"
STRIPE_PUBLISHABLE_KEY="pk_live_xxxxxxxxxxxxxxxxxxxxxxxx"
STRIPE_WEBHOOK_SECRET="whsec_xxxxxxxxxxxxxxxxxxxx"
STRIPE_MODE="live"`;

  const restartCmd = "sudo supervisorctl restart backend";
  const webhookEndpoint = `${window.location.origin}/api/webhook/stripe`;

  return (
    <>
      <Card data-testid="stripe-status-card" className="p-5 border-2 border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#0C2E2B]" />
            <h3 className="font-display font-black text-[#0C2E2B]">Stripe</h3>
          </div>
          <button data-testid="stripe-refresh-btn" onClick={load} className="text-zinc-400 hover:text-[#0C2E2B]">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading ? (
          <div className="text-xs text-zinc-500 mt-3">Loading...</div>
        ) : !status ? (
          <div className="text-xs text-red-600 mt-3">Could not reach Stripe</div>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className={isLive ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"}>
                {isLive ? "LIVE" : "TEST MODE"}
              </Badge>
              <Badge variant="outline" className={status.reachable ? "border-emerald-500 text-emerald-700" : "border-red-500 text-red-700"}>
                {status.reachable ? "API reachable" : "API unreachable"}
              </Badge>
              <Badge variant="outline" className={status.webhook_configured ? "border-emerald-500 text-emerald-700" : "border-zinc-300 text-zinc-500"}>
                Webhook · {status.webhook_configured ? "set" : "off"}
              </Badge>
            </div>
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-zinc-500">Account</span>
                <span className="font-mono-data text-[#0C2E2B] truncate max-w-[180px]">{status.account?.id || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Charges</span>
                <span className={status.account?.charges_enabled ? "text-emerald-700 font-bold" : "text-zinc-500"}>
                  {status.account?.charges_enabled ? "enabled" : "disabled"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Payouts</span>
                <span className={status.account?.payouts_enabled ? "text-emerald-700 font-bold" : "text-zinc-500"}>
                  {status.account?.payouts_enabled ? "enabled" : "disabled"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Key prefix</span>
                <span className="font-mono-data">{status.key_prefix}</span>
              </div>
            </div>

            {!isLive && (
              <Button
                data-testid="stripe-go-live-btn"
                onClick={() => setShowWizard(true)}
                className="mt-4 btn-kinetic rounded-md w-full text-xs h-auto py-2 whitespace-normal leading-tight"
              >
                Go Live · Charge Real Cards
              </Button>
            )}
            {isLive && (
              <div className="mt-4 bg-emerald-50 border border-emerald-500 text-emerald-900 rounded-md p-2 text-[11px] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Live mode active — real cards are being charged.
              </div>
            )}
          </>
        )}
      </Card>

      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0C2E2B]">Go Live · 4 Steps</DialogTitle>
            <DialogDescription>Follow these in order. Nothing on your site breaks while you do it — test mode stays live until step 4.</DialogDescription>
          </DialogHeader>

          <ol className="mt-4 space-y-4">
            {/* STEP 1 */}
            <li data-testid="stripe-wizard-step-1" className="bg-zinc-50 border-2 border-zinc-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#FFDF00] text-[#0C2E2B] grid place-items-center font-black text-xs">1</div>
                <div className="font-bold text-sm text-[#0C2E2B]">Claim your Stripe account</div>
              </div>
              <p className="text-xs text-zinc-600 mb-3">Opens Stripe onboarding in a new tab. You'll enter your business details, tax info, and bank account for payouts.</p>
              {status?.onboarding_url && (
                <a
                  href={status.onboarding_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="stripe-wizard-onboarding-link"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FFDF00] text-[#0C2E2B] font-bold text-xs hover:bg-[#E5C900]"
                >
                  Open Stripe onboarding <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </li>

            {/* STEP 2 */}
            <li data-testid="stripe-wizard-step-2" className="bg-zinc-50 border-2 border-zinc-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#FFDF00] text-[#0C2E2B] grid place-items-center font-black text-xs">2</div>
                <div className="font-bold text-sm text-[#0C2E2B]">Get your live API keys</div>
              </div>
              <p className="text-xs text-zinc-600 mb-2">
                After onboarding, go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#0C2E2B] underline font-bold">dashboard.stripe.com/apikeys</a>,
                switch the toggle from <b>Test</b> to <b>Live</b>, and copy your Publishable and Secret keys.
              </p>
              <p className="text-xs text-zinc-600">
                Then go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-[#0C2E2B] underline font-bold">dashboard.stripe.com/webhooks</a>,
                add an endpoint pointing to:
              </p>
              <div className="mt-2 flex items-center gap-2 bg-white border border-zinc-200 rounded-md px-3 py-2">
                <code className="font-mono-data text-xs text-[#0C2E2B] flex-1 truncate">{webhookEndpoint}</code>
                <button
                  data-testid="stripe-wizard-copy-webhook"
                  onClick={() => copy("webhook", webhookEndpoint)}
                  className="text-zinc-500 hover:text-[#0C2E2B]"
                >
                  {copied.webhook ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Select events: <span className="font-mono-data">checkout.session.completed, checkout.session.async_payment_succeeded, checkout.session.async_payment_failed, checkout.session.expired, charge.refunded</span>. Copy the "Signing secret" (<span className="font-mono-data">whsec_...</span>) too.</p>
            </li>

            {/* STEP 3 */}
            <li data-testid="stripe-wizard-step-3" className="bg-zinc-50 border-2 border-zinc-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#FFDF00] text-[#0C2E2B] grid place-items-center font-black text-xs">3</div>
                <div className="font-bold text-sm text-[#0C2E2B]">Paste them into backend/.env</div>
              </div>
              <p className="text-xs text-zinc-600 mb-2">Open <span className="font-mono-data">/app/backend/.env</span> in Emergent's file editor and replace these four values:</p>
              <div className="relative bg-[#0C2E2B] rounded-md p-3">
                <pre className="text-[11px] text-[#FFDF00] font-mono-data whitespace-pre-wrap break-all">{envSnippet}</pre>
                <button
                  data-testid="stripe-wizard-copy-env"
                  onClick={() => copy("env", envSnippet)}
                  className="absolute top-2 right-2 bg-[#FFDF00] text-[#0C2E2B] px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 hover:bg-[#E5C900]"
                >
                  {copied.env ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
            </li>

            {/* STEP 4 */}
            <li data-testid="stripe-wizard-step-4" className="bg-zinc-50 border-2 border-zinc-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#FFDF00] text-[#0C2E2B] grid place-items-center font-black text-xs">4</div>
                <div className="font-bold text-sm text-[#0C2E2B]">Restart the backend & verify</div>
              </div>
              <p className="text-xs text-zinc-600 mb-2">The backend auto-restarts when <span className="font-mono-data">.env</span> changes. If it doesn't, run:</p>
              <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-md px-3 py-2">
                <code className="font-mono-data text-xs text-[#0C2E2B] flex-1">{restartCmd}</code>
                <button
                  data-testid="stripe-wizard-copy-restart"
                  onClick={() => copy("restart", restartCmd)}
                  className="text-zinc-500 hover:text-[#0C2E2B]"
                >
                  {copied.restart ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <Button
                data-testid="stripe-wizard-recheck-btn"
                onClick={async () => { await load(); toast.success("Rechecked Stripe status"); }}
                className="mt-3 btn-kinetic rounded-md w-full text-xs"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recheck Stripe status
              </Button>
              <p className="text-[11px] text-zinc-500 mt-2 text-center">The card above will flip from <b>TEST MODE</b> to <b>LIVE</b> and show your real account.</p>
            </li>
          </ol>

          <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 text-amber-900 p-3 rounded text-[11px]">
            <b>Heads up:</b> Once you flip to live, real cards will be charged. Test with a small purchase first ($1 merch item works). No customer data or bookings are lost — Zelle orders keep working exactly the same.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function WeeklyDigestCard() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const showPreview = async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/digest/preview");
      setPreview(data);
      setOpen(true);
    } catch { toast.error("Could not load digest"); }
    finally { setLoading(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await adminClient.post("/admin/digest/send-now");
      toast.success("Digest sent (or persisted if email keys are still pending)");
      setOpen(false);
      window.dispatchEvent(new CustomEvent("hp:alerts-changed"));
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <>
      <Card data-testid="digest-card" className="p-5 border-2 border-zinc-200">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0C2E2B]" />
          <h3 className="font-display font-black text-[#0C2E2B]">Weekly Cash Digest</h3>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Fires <b>Mondays 5:00 AM CT</b> to your alert email.
          Includes paid revenue, pending Zelle deposits, and top-line rows.
        </p>
        <div className="mt-4 flex gap-2">
          <Button data-testid="digest-preview-btn" onClick={showPreview} disabled={loading} className="btn-kinetic rounded-md flex-1 text-xs">
            <Eye className="w-3.5 h-3.5 mr-1" /> {loading ? "Loading..." : "Preview"}
          </Button>
          <Button
            data-testid="digest-send-now-btn"
            variant="outline"
            onClick={sendNow}
            disabled={sending}
            className="rounded-md flex-1 text-xs"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending..." : "Send now"}
          </Button>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Digest Preview</DialogTitle>
            <DialogDescription>This is exactly what lands in your inbox every Monday.</DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Card className="p-4 bg-[#FFDF00]/20 border-2 border-[#FFDF00]">
                  <div className="text-[10px] font-tactical uppercase tracking-widest text-[#0C2E2B]">Paid this week</div>
                  <div className="font-display font-black text-2xl text-[#0C2E2B] mt-1" data-testid="digest-total-paid">${preview.digest.total_revenue?.toFixed(2)}</div>
                  <div className="text-[10px] text-[#0C2E2B]/70">{preview.digest.paid_order_count} orders · {preview.digest.paid_booking_count} bookings</div>
                </Card>
                <Card className="p-4 bg-red-50 border-2 border-red-500">
                  <div className="text-[10px] font-tactical uppercase tracking-widest text-red-900">Awaiting Zelle</div>
                  <div className="font-display font-black text-2xl text-red-900 mt-1" data-testid="digest-pending">${preview.digest.pending_amount?.toFixed(2)}</div>
                  <div className="text-[10px] text-red-900/70">{preview.digest.pending_orders?.length || 0} orders · {preview.digest.pending_bookings?.length || 0} bookings</div>
                </Card>
              </div>
              <div className="border-2 border-zinc-200 rounded-md overflow-hidden">
                <iframe
                  title="digest-html"
                  srcDoc={preview.html}
                  className="w-full h-[520px] border-0 bg-white"
                  sandbox=""
                  data-testid="digest-html-preview"
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Dashboard() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/alerts?limit=100");
      setAlerts(data.items || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("hp:alerts-changed", handler);
    return () => window.removeEventListener("hp:alerts-changed", handler);
  }, [load]);

  const markRead = async (id) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    try {
      await adminClient.post(`/admin/alerts/${id}/read`);
      window.dispatchEvent(new CustomEvent("hp:alerts-changed"));
    } catch { toast.error("Could not mark read"); load(); }
  };

  const markAllRead = async () => {
    const unread = alerts.filter((a) => !a.read);
    if (unread.length === 0) return;
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    try {
      await Promise.all(unread.map((a) => adminClient.post(`/admin/alerts/${a.id}/read`)));
      toast.success(`Marked ${unread.length} alerts read`);
      window.dispatchEvent(new CustomEvent("hp:alerts-changed"));
    } catch { load(); }
  };

  const visible = useMemo(() => filter === "all" ? alerts : alerts.filter((a) => a.kind === filter), [alerts, filter]);
  const unreadCount = useMemo(() => alerts.filter((a) => !a.read).length, [alerts]);
  const kindFilters = [
    { key: "all", label: "All" },
    { key: "booking_created", label: "Bookings" },
    { key: "order_created", label: "Orders" },
    { key: "inquiry_created", label: "Inquiries" },
    { key: "damage_claim", label: "Claims" },
    { key: "weekly_digest", label: "Digests" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">Dashboard</h1>
          <p className="text-sm text-zinc-500">Everything that's happened on your site.</p>
        </div>
        <button data-testid="dashboard-refresh-btn" onClick={load} className="flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-[#0C2E2B]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mb-6"><IncomeCard /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <AlertSettingsCard />
        <CustomerEmailsCard />
        <CalendarSyncCard />
        <StripeStatusCard />
        <WeeklyDigestCard />
      </div>

      <Card className="border-2 border-zinc-200 overflow-hidden">
        <div className="p-4 flex items-center justify-between flex-wrap gap-2 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#0C2E2B]" />
            <h2 className="font-display font-black text-[#0C2E2B]">Alert Feed</h2>
            {unreadCount > 0 && (
              <Badge data-testid="dashboard-unread-badge" className="bg-red-500 hover:bg-red-600 text-white">
                {unreadCount} new
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {kindFilters.map((f) => (
              <button
                key={f.key}
                data-testid={`alert-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                  filter === f.key ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                }`}
              >
                {f.label}
              </button>
            ))}
            {unreadCount > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAllRead}
                className="ml-2 text-[10px] font-bold text-[#0C2E2B] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-zinc-500">Loading alerts...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">
            <Bell className="w-8 h-8 mx-auto text-zinc-300" />
            <p className="text-sm font-bold mt-3">No alerts here yet.</p>
            <p className="text-xs mt-1">Bookings, orders, and inquiries will show up as they roll in.</p>
          </div>
        ) : (
          <div>
            {visible.map((a) => <AlertRow key={a.id} alert={a} onMarkRead={markRead} />)}
          </div>
        )}
      </Card>
    </div>
  );
}
